<template>
  <div class="py-4 container-fluid">
    <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
      <div>
        <p class="text-xs text-uppercase font-weight-bolder mb-1 overview-eyebrow">
          {{ $t("key_dashboard_my_business") }}
        </p>
        <h3 class="mb-1 font-weight-bolder">{{ $t("key_dashboard_greeting", { name: userName }) }}</h3>
        <p class="text-sm text-secondary mb-0">{{ $t("key_dashboard_subtitle") }}</p>
      </div>
      <div class="period-selector">
        <i class="fa fa-calendar me-2 text-secondary" aria-hidden="true"></i>
        <select
          class="form-select form-select-sm"
          :value="days"
          :aria-label="$t('key_dashboard_period')"
          @change="changePeriod($event.target.value)"
        >
          <option v-for="option in periods" :key="option" :value="option">
            {{ periodLabel(option) }}
          </option>
        </select>
      </div>
    </div>

    <KpiCards :kpis="kpis.data" :loading="kpis.loading" />

    <div class="row mt-4">
      <div class="col-xl-4 col-lg-6 mb-4">
        <OrderStatusChart v-bind="orderStatus" />
      </div>
      <div class="col-xl-5 col-lg-6 mb-4">
        <PreparationTime v-bind="preparationTime" />
      </div>
      <div class="col-xl-3 col-lg-12 mb-4">
        <ShipmentsByCourier v-bind="shipmentsByCourier" />
      </div>
    </div>

    <div class="row">
      <div class="col-xl-5 col-lg-6 mb-4">
        <StockBreaks v-bind="stockBreaks" />
      </div>
      <div class="col-xl-4 col-lg-6 mb-4">
        <TopProducts v-bind="topProducts" />
      </div>
      <div class="col-xl-3 col-lg-12 mb-4">
        <OverviewAlerts v-bind="alerts" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import KpiCards from "./KpiCards.vue";
import OrderStatusChart from "./OrderStatusChart.vue";
import OverviewAlerts from "./OverviewAlerts.vue";
import PreparationTime from "./PreparationTime.vue";
import ShipmentsByCourier from "./ShipmentsByCourier.vue";
import StockBreaks from "./StockBreaks.vue";
import TopProducts from "./TopProducts.vue";
import { COLORS } from "./shared";

// Periods the back accepts (?days=): only yesterday, or the last N days
// ending today.
const YESTERDAY = "yesterday";
const PERIODS = [YESTERDAY, "7", "30", "90"];

// The period from the ?days= query param, or null when missing or not accepted.
function periodFromQuery(value) {
  return PERIODS.includes(value) ? value : null;
}

// The v2 customer dashboard. Every block loads on its own; see
// docs/dashboard.md in the back for what each one counts.
export default {
  name: "DashboardOverview",
  components: {
    KpiCards,
    OrderStatusChart,
    OverviewAlerts,
    PreparationTime,
    ShipmentsByCourier,
    StockBreaks,
    TopProducts,
  },
  data() {
    return { periods: PERIODS, eyebrowColor: COLORS.green };
  },
  computed: {
    ...mapState("dashboardOverview", [
      "days",
      "kpis",
      "orderStatus",
      "preparationTime",
      "shipmentsByCourier",
      "stockBreaks",
      "topProducts",
      "alerts",
    ]),
    userName() {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        return (user?.user || "").split("@")[0];
      } catch {
        return "";
      }
    },
  },
  watch: {
    // The period lives in the URL so a dashboard link can be shared.
    "$route.query.days"(value) {
      const days = periodFromQuery(value);
      if (days && days !== this.days) {
        this.$store.dispatch("dashboardOverview/loadPeriod", days);
      }
    },
  },
  mounted() {
    const days = periodFromQuery(this.$route.query.days) || this.days;
    this.$store.commit("dashboardOverview/setDays", days);
    this.$store.dispatch("dashboardOverview/loadAll");
    this.setQueryPeriod(days);
  },
  methods: {
    periodLabel(days) {
      return days === YESTERDAY
        ? this.$t("key_dashboard_yesterday")
        : this.$t("key_dashboard_last_days", { days });
    },
    changePeriod(days) {
      this.setQueryPeriod(days);
    },
    setQueryPeriod(days) {
      if (this.$route.query.days !== days) {
        this.$router.replace({ query: { ...this.$route.query, days } });
      }
    },
  },
};
</script>

<style scoped>
.overview-eyebrow {
  color: v-bind(eyebrowColor);
  letter-spacing: 0.08em;
}

.period-selector {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}

.period-selector .form-select {
  border: 0;
  box-shadow: none;
  min-width: 10rem;
}
</style>
